import { useEffect, useState, useCallback } from 'react';
import {
  Phone, RefreshCw, Calendar, Users, MapPin, Mail, Tag, Loader2, PartyPopper,
} from 'lucide-react';
import { listLeads, updateLeadStatus } from '../api';
import type { PhoneLead, PhoneLeadsResponse, LeadEstatus } from '../types';

const ESTATUS: { key: LeadEstatus; label: string }[] = [
  { key: 'nuevo', label: 'Nuevo' },
  { key: 'contactado', label: 'Contactado' },
  { key: 'cotizado', label: 'Cotizado' },
  { key: 'ganado', label: 'Ganado' },
  { key: 'perdido', label: 'Perdido' },
];

const ESTATUS_STYLE: Record<string, string> = {
  nuevo: 'bg-primary/15 text-primary border-primary/30',
  contactado: 'bg-blue-500/15 text-blue-500 border-blue-500/30',
  cotizado: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
  ganado: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  perdido: 'bg-muted text-muted-foreground border-border',
};

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-MX', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function LeadsView() {
  const [data, setData] = useState<PhoneLeadsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('');
  const [savingId, setSavingId] = useState<string | null>(null);

  const load = useCallback(async (est?: string) => {
    setLoading(true);
    setError(null);
    try {
      setData(await listLeads(est || undefined));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error cargando leads');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(filter); }, [load, filter]);

  async function onStatus(lead: PhoneLead, est: LeadEstatus) {
    if (est === lead.estatus) return;
    setSavingId(lead.id);
    try {
      await updateLeadStatus(lead.id, est);
      await load(filter);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo actualizar');
    } finally {
      setSavingId(null);
    }
  }

  const leads = data?.leads ?? [];
  const byStatus = data?.by_status ?? {};
  const totalAll = Object.values(byStatus).reduce((a, b) => a + b, 0);

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
            <Phone className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground leading-tight">Leads del teléfono</h1>
            <p className="text-sm text-muted-foreground">Prospectos que capturó el agente de llamadas</p>
          </div>
        </div>
        <button
          onClick={() => load(filter)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-card
                     text-sm text-foreground hover:bg-muted transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {/* Filtro por estatus (chips) */}
      <div className="flex flex-wrap gap-2 mb-5">
        <button
          onClick={() => setFilter('')}
          className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
            filter === '' ? 'bg-primary/15 text-primary border-primary/30'
                          : 'bg-card text-muted-foreground border-border hover:bg-muted'
          }`}
        >
          Todos <span className="tabular-nums opacity-70">({totalAll})</span>
        </button>
        {ESTATUS.map((s) => (
          <button
            key={s.key}
            onClick={() => setFilter(s.key)}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
              filter === s.key ? ESTATUS_STYLE[s.key]
                               : 'bg-card text-muted-foreground border-border hover:bg-muted'
            }`}
          >
            {s.label} <span className="tabular-nums opacity-70">({byStatus[s.key] ?? 0})</span>
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-500">
          {error}
        </div>
      )}

      {/* Estado vacío */}
      {!loading && leads.length === 0 && !error && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <PartyPopper className="w-10 h-10 text-muted-foreground mb-3" />
          <p className="text-foreground font-medium">Aún no hay leads{filter ? ' con ese estatus' : ''}</p>
          <p className="text-sm text-muted-foreground mt-1">
            Cuando alguien llame y el agente capture sus datos, aparecerán aquí.
          </p>
        </div>
      )}

      {/* Loading inicial */}
      {loading && !data && (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      )}

      {/* Lista de leads (tarjetas) */}
      <div className="grid gap-3">
        {leads.map((l) => (
          <div key={l.id} className="rounded-xl border border-border bg-card p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-foreground">{l.nombre || 'Sin nombre'}</span>
                  {l.tipo_evento && (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                      <Tag className="w-3 h-3" /> {l.tipo_evento}
                    </span>
                  )}
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${ESTATUS_STYLE[l.estatus] || ESTATUS_STYLE.nuevo}`}>
                    {ESTATUS.find((e) => e.key === l.estatus)?.label ?? l.estatus}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
                  {l.fecha_evento && <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {l.fecha_evento}</span>}
                  {l.personas != null && <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {l.personas} pers.</span>}
                  {l.espacio && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {l.espacio}</span>}
                </div>
                <div className="mt-1.5 flex flex-wrap gap-x-5 gap-y-1 text-sm">
                  {l.telefono && (
                    <a href={`tel:${l.telefono}`} className="inline-flex items-center gap-1 text-primary hover:underline">
                      <Phone className="w-3.5 h-3.5" /> {l.telefono}
                    </a>
                  )}
                  {l.email && (
                    <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 text-primary hover:underline">
                      <Mail className="w-3.5 h-3.5" /> {l.email}
                    </a>
                  )}
                </div>
                {l.nota && <p className="mt-2 text-sm text-muted-foreground italic">{l.nota}</p>}
              </div>

              <div className="flex flex-col items-end gap-2">
                <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">{fmtDate(l.created_at)}</span>
                <select
                  value={l.estatus}
                  disabled={savingId === l.id}
                  onChange={(e) => onStatus(l, e.target.value as LeadEstatus)}
                  className="text-sm rounded-lg border border-border bg-card text-foreground px-2 py-1
                             focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  {ESTATUS.map((s) => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
